import React, { useEffect, useRef } from "react";
import { paper, Point, Size, Rectangle, Path, PointText } from "paper";
import Footer from "../../layout/Footer";
import Header from "../../layout/Header";
import { colors } from "../../../helpers/colors";
import DownloadMenu from "../../shared/DownloadMenu";
import { shaftAsf, formatAsf } from "../../shared/asf";
import { openTrueSpace, projectPointFromEvent, hitsLink, drawTrueSpaceNote } from "../../shared/trueSpace";

const Output = (props) => {
  let multiplier = 5;
  let padding = 100;
  let points = -160;
  const dim = props.dimensions;
  let D = parseFloat(dim.direction);
  let R = parseFloat(dim.radius) * multiplier;
  let Dmax = parseFloat(dim.dmax) * multiplier;
  let Dmin = parseFloat(dim.dmin) * multiplier;
  let Dis = parseFloat(dim.dis) * multiplier;
  let Position = dim.position;
  let text;
  let i = 0;
  let Arc = (Math.PI * R) / 2;
  let ArcFull = Arc * 2;
  let TanAmtLinear = (Dmax - Dmin) / (R * 2);
  let Amt = toDegree(Math.atan(TanAmtLinear));
  if (Math.abs(Amt - 90) < 0.01) {
    Amt = 89.5;
  }
  const DisAngle = Dis * (90 / Arc);
  let Dir = Position === "pleft" ? D - DisAngle : D + DisAngle;
  Dir = ((Dir % 360) + 360) % 360;
  let StartDth = Dmin - 10 * multiplier;
  let EndDth = Dmax + 10 * multiplier;
  let length = EndDth - StartDth;
  let Theta = 0;
  let scalearr = [];
  function toRadians(angle) {
    return angle * (Math.PI / 180);
  }
  function toDegree(radian) {
    return radian * (180 / Math.PI);
  }
  // Box of the TrueSpace link drawn in the output notes, in project
  // coordinates. Set while drawing; read by the canvas mouse handlers below.
  const trueSpaceLinkBounds = useRef(null);

  const canvasProjectPoint = (e) => {
    const view = paper.view;
    if (!view) return null;
    return projectPointFromEvent(e, e.currentTarget.getBoundingClientRect(), view.viewSize.width);
  };

  const handleCanvasClick = (e) => {
    if (hitsLink(canvasProjectPoint(e), trueSpaceLinkBounds.current)) openTrueSpace();
  };

  const handleCanvasMouseMove = (e) => {
    const over = hitsLink(canvasProjectPoint(e), trueSpaceLinkBounds.current);
    e.currentTarget.style.cursor = over ? "pointer" : "default";
  };

  useEffect(() => {
    if (!props.showdebug) {
      paper.setup("paper-canvas");
      creategraph();
    }
  }, [props.dimensions, props.showdebug]);
  const creategraph = () => {
    paper.project.clear();
    let width = ArcFull * 2 + 850;
    let height = length + 400;
    paper.view.viewSize = new Size(width, height);
    draw();
    paper.view.draw();
  };
  const createScale = () => {
    let Xinit = 4 * Arc + padding;
    for (i = padding; i <= length + padding; i += multiplier * 10) {
      text = new PointText(new Point(Xinit + 15, i));
      text.justification = "center";
      text.fillColor = "black";
      const content = (i - padding + StartDth) / multiplier;
      text.content = content;
      scalearr.push([Xinit + 10, i]);
      scalearr.push([Xinit, i]);
      if (i + multiplier * 10 <= length + padding) {
        for (let j = 1; j <= 9; j++) {
          scalearr.push([Xinit, i + multiplier * j]);
          scalearr.push([Xinit + 5, i + multiplier * j]);
          scalearr.push([Xinit, i + multiplier * j]);
        }
        scalearr.push([Xinit, i + multiplier * 10]);
      }
    }
    new Path({
      segments: scalearr,
      strokeColor: "black",
      strokeWidth: 1,
    });
  };
  const createLegend = ({ path, directionarrowline }) => {
    text = new PointText(path.bounds.bottomRight.add([15, 25]));
    text.justification = "left";
    text.fillColor = "black";
    text.content = "Azimuth";
    text = new PointText(directionarrowline.bounds.topLeft.add([0, -15]));
    text.justification = "center";
    text.fillColor = "black";
    text.content = "View Direction N" + String(Math.round(D)).padStart(3, "0") + "\xB0";
    text = new PointText(path.bounds.rightCenter.add([28, -40]));
    text.justification = "left";
    text.fillColor = "black";
    text.content = "Depth";
  };
  const createLog = () => {
    let style = {
      fillColor: "white",
      strokeColor: "black",
      strokeWidth: 1,
    };
    var path = new Path.Rectangle(new Rectangle(new Point(padding, padding), new Size(4 * Arc, length)));
    path.style = style;
    var path1 = new Path.Rectangle(new Rectangle(new Point(padding + Arc, padding), new Size(2 * Arc, length)));
    path1.style = style;

    var path21 = new Path.Circle(path.bounds.bottomCenter.add([0, R + 85]), R);
    path21.style = style;

    var path3 = new Path.Line(path.bounds.topCenter, path21.bounds.bottomCenter);
    path3.strokeColor = "black";
    path3.dashArray = [4, 10];

    var directionarrowline = new Path.Line(path.bounds.topCenter.add([0, -(R / 2 + 10)]), path.bounds.topCenter.add([0, -(R / 2 + 30)]));
    directionarrowline.style = style;
    var directionarrowht = new Path.Line(path.bounds.topCenter.add([0, -(R / 2 + 30)]), path.bounds.topCenter.add([0, -(R / 2 + 20)]));
    directionarrowht.style = style;
    directionarrowht.rotate(45, path.bounds.topCenter.add([0, -(R / 2 + 30)]));
    var directionarrowhb = new Path.Line(path.bounds.topCenter.add([0, -(R / 2 + 30)]), path.bounds.topCenter.add([0, -(R / 2 + 20)]));
    directionarrowhb.style = style;
    directionarrowhb.rotate(315, path.bounds.topCenter.add([0, -(R / 2 + 30)]));



    var path4 = new Path.Arc(path.bounds.bottomRight, path21.bounds.bottomCenter.add([R * 3, -(R * 2)]), path21.bounds.bottomCenter);
    path4.strokeColor = "black";
    path4.dashArray = [2, 2];
    var path5 = new Path.Arc(path.bounds.bottomLeft, path21.bounds.bottomCenter.add([-R * 3, -(R * 2)]), path21.bounds.bottomCenter);
    path5.strokeColor = "black";
    path5.dashArray = [2, 2];
    var path6 = new Path.Arc(path1.bounds.bottomLeft, path21.bounds.leftCenter.add([-25, -R]), path21.bounds.leftCenter);
    path6.strokeColor = "black";
    path6.dashArray = [2, 2];
    var path7 = new Path.Arc(path1.bounds.bottomRight, path21.bounds.rightCenter.add([25, -R]), path21.bounds.rightCenter);
    path7.strokeColor = "black";
    path7.dashArray = [2, 2];
    scalearr = [];
    let counter = 0;
    for (i = padding; i <= 4 * Arc + padding; i += (4 * Arc) / 36) {
      if (counter % 90 == 0) {
        text = new PointText(new Point(i, padding + length + 25));
        text.justification = "center";
        text.fillColor = "black";
        let content = "";
        if (counter === 0 || counter === 360) {
          content = (D > 180 ? D - 180 : D + 180) + "\xB0";
        } else if (counter === 180 || counter === 270 || counter === 90) {
          let degreeToCalculate = D - 180 + counter;
          if (degreeToCalculate < 0) {
            degreeToCalculate = 360 + degreeToCalculate;
          }
          if (degreeToCalculate > 360) {
            degreeToCalculate = degreeToCalculate - 360;
          }
          content = degreeToCalculate + "\xB0";
        }
        text.content = content;
        text.rotate(90);
        // Rotated labels are tall; anchor each label's top just below the tick
        // marks (which occupy padding+length .. padding+length+10) so the
        // labels extend downward toward the plan circle instead of crossing up
        // into the frame.
        text.bounds.top = padding + length + 14;
      }
      counter += 10;
      scalearr.push([i, padding + length]);
      scalearr.push([[i, padding + length + 10]]);
      scalearr.push([[i, padding + length]]);
      scalearr.push([i + (4 * Arc) / 36, padding + length]);
    }
    new Path({
      segments: scalearr,
      strokeColor: "black",
      strokeWidth: 1,
    });

    // Top distance scale: 0 at centerline, ±πR (meters) at edges
    const topscalearr = [];
    let topCounter = 0;
    for (let j = padding; j <= 4 * Arc + padding; j += (4 * Arc) / 36) {
      if (topCounter % 90 === 0) {
        const distFromCenter = (j - (padding + 2 * Arc)) / multiplier;
        const topText = new PointText(new Point(j, padding - 18));
        topText.justification = "center";
        topText.fillColor = "black";
        topText.content = distFromCenter.toFixed(1).replace(/\.0$/, "") + "m";
        topText.rotate(90);
        // Rotated labels are tall; anchor each label's bottom just above the
        // tick marks (which occupy padding-10 .. padding) so long labels like
        // "-31.4m" extend upward into the free header space instead of crossing
        // down into the frame.
        topText.bounds.bottom = padding - 14;
      }
      topCounter += 10;
      topscalearr.push([j, padding]);
      topscalearr.push([[j, padding - 10]]);
      topscalearr.push([[j, padding]]);
      topscalearr.push([j + (4 * Arc) / 36, padding]);
    }
    new Path({
      segments: topscalearr,
      strokeColor: "black",
      strokeWidth: 1,
    });

    createLegend({ path, directionarrowline });
  };
  const createTrace = () => {
    const index = 0;
    let style = {
      fillColor: "transparent",
      strokeColor: "black",
      strokeWidth: 1,
    };
    style.strokeColor = colors[index];
    const TanAmt = Math.tan(toRadians(Amt));
    let Dth = Dmin;
    let ArcL = (Math.PI * R) / 2;
    let Xmin, Xmax;
    let traceunion = [];
    let XPmid = ArcL * 2;
    let YPmid = padding + R * TanAmt;
    let XP2 = padding + ArcL;
    let XP4 = padding + ArcL * 3;
    let diff = D - Dir;
    let toMove = (diff * 4 * ArcL) / 360;
    let centerLog = padding + 2 * ArcL;
    new Path.Line(new Point(centerLog + toMove, padding), new Point(centerLog + toMove, padding + length));
    var tmppath = new Path.Rectangle(new Rectangle(new Point(padding + toMove, padding), new Size(4 * ArcL, length)));
    if (Dth < length / 2) {
      YPmid += Dth;
    } else {
      YPmid += Dth - StartDth;
    }
    let XP5 = [];
    let YP5 = [];
    let H = [];
    for (let n = 90; n >= 0; n--) {
      H[n] = R * Math.sin(toRadians(n));
      XP5[n] = XP2 - ArcL * (n / 90);
      YP5[n] = YPmid - H[n] * TanAmt;
      if (Xmin) {
        Xmin = Math.min(Xmin, XP5[n]);
      } else {
        Xmin = XP5[n];
      }
      if (Xmax) {
        Xmax = Math.max(Xmax, XP5[n]);
      } else {
        Xmax = XP5[n];
      }
      traceunion.push([XP5[n], YP5[n]]);
    }
    for (let n = 0; n <= 90; n++) {
      H[n] = R * Math.sin(toRadians(n));
      XP5[n] = XP2 + ArcL * (n / 90);
      YP5[n] = YPmid + H[n] * TanAmt;
      if (Xmin) {
        Xmin = Math.min(Xmin, XP5[n]);
      } else {
        Xmin = XP5[n];
      }
      if (Xmax) {
        Xmax = Math.max(Xmax, XP5[n]);
      } else {
        Xmax = XP5[n];
      }
      traceunion.push([XP5[n], YP5[n]]);
    }
    for (let n = 90; n >= 0; n--) {
      H[n] = R * Math.sin(toRadians(n));
      XP5[n] = XP4 - ArcL * (n / 90);
      YP5[n] = YPmid + H[n] * TanAmt;
      traceunion.push([XP5[n], YP5[n]]);
    }
    for (let n = Theta; n <= 90; n++) {
      H[n] = R * Math.sin(toRadians(n));
      XP5[n] = XP4 + ArcL * (n / 90);
      YP5[n] = YPmid - H[n] * TanAmt;
      if (Xmin) {
        Xmin = Math.min(Xmin, XP5[n]);
      } else {
        Xmin = XP5[n];
      }
      if (Xmax) {
        Xmax = Math.max(Xmax, XP5[n]);
      } else {
        Xmax = XP5[n];
      }
      traceunion.push([XP5[n], YP5[n]]);
    }
    let union = new Path({
      segments: traceunion,
      style: style,
    });
    let interx = union.getIntersections(tmppath);
    if (interx.length === 1) {
      let firstoffset = union.getOffsetOf(interx[0].point);
      let firstcut = union.splitAt(firstoffset);
      if (!firstcut) {
        return;
      }
      let firstcutx = firstcut.position.x;
      let firstcuty = firstcut.position.y;
      let unionx = union.position.x;
      let uniony = union.position.y;
      if (toMove > 0) {
        firstcut.position = new Point(firstcutx - toMove, firstcuty);
        union.position = new Point(unionx + (4 * ArcL - toMove), uniony);
      } else {
        firstcut.position = new Point(firstcutx - (4 * ArcL + toMove), firstcuty);
        union.position = new Point(unionx - toMove, uniony);
      }
    }
    const usrvar = {};
    usrvar["TanAmt"] = TanAmt.toFixed(1).replace(/\.0$/, "");
    usrvar["Shaft Radius"] = R / multiplier;
    usrvar["View Direction"] = D;
    usrvar["Dth"] = Dth / multiplier;
    usrvar["P1"] =
      "(0," +
      ((R * TanAmt - R * Math.sin(toRadians(90)) * TanAmt + Dth) / multiplier).toFixed(1).replace(/\.0$/, "") +
      ")";
    usrvar["P2"] =
      "(" +
      ((XP2 - padding) / multiplier).toFixed(1).replace(/\.0$/, "") +
      "," +
      ((R * TanAmt + Dth) / multiplier).toFixed(1).replace(/\.0$/, "") +
      ")";
    usrvar["P3"] =
      "(" +
      (XPmid / multiplier).toFixed(1).replace(/\.0$/, "") +
      "," +
      ((R * TanAmt - R * Math.sin(toRadians(90)) * TanAmt) / multiplier).toFixed(1).replace(/\.0$/, "") +
      ")";
    usrvar["P4"] =
      "(" +
      ((XP4 - padding) / multiplier).toFixed(1).replace(/\.0$/, "") +
      "," +
      ((R * TanAmt + Dth) / multiplier).toFixed(1).replace(/\.0$/, "") +
      ")";
    usrvar["Bottom of trace"] = ((Dth + R * 2 * TanAmt) / multiplier).toFixed(1).replace(/\.0$/, "");
    usrvar["Dir"] = String(Math.round(Dir)).padStart(3, "0") + "\xB0";
    usrvar["Amt"] = Amt.toFixed(1).replace(/\.0$/, "");
    usrvar["Arc"] = (ArcL / multiplier).toFixed(1).replace(/\.0$/, "");
    usrvar["Trace Span"] = parseFloat(usrvar["Bottom of trace"] - Dth / multiplier)
      .toFixed(1)
      .replace(/\.0$/, "");
    // ---- Tabulated Inputs / Outputs ----
    const positionLabelMap = { pleft: "Left of Centerline", pright: "Right of Centerline" };
    const inputRows = [{
      no: "1",
      dmax: String(dim.dmax),
      dmin: String(dim.dmin),
      dis: String(dim.dis),
      pos: positionLabelMap[dim.position] || dim.position,
      color: colors[index],
    }];
    const outputRows = [{
      no: "1",
      dir: "N" + usrvar["Dir"],
      amt: usrvar["Amt"] + "\xB0",
      tsp: String(usrvar["Trace Span"]),
      asf: formatAsf(shaftAsf(Amt)),
      color: colors[index],
    }];

    const drawBorder = (x1, y1, x2, y2) => {
      const p = new Path.Line(new Point(x1, y1), new Point(x2, y2));
      p.strokeColor = "black";
      p.strokeWidth = 0.5;
      return p;
    };
    const cellText = (txt, xL, xR, yT, yB, opts = {}) => {
      const t = new PointText(new Point((xL + xR) / 2, (yT + yB) / 2 + 3));
      t.justification = "center";
      t.fillColor = opts.color || "black";
      if (opts.bold) t.fontWeight = "bold";
      t.fontSize = opts.fontSize || 10;
      t.content = txt;
      return t;
    };
    const buildCols = (xStart, defs) => {
      let x = xStart;
      return defs.map((d) => {
        const col = { ...d, left: x, right: x + d.width };
        x += d.width;
        return col;
      });
    };
    // Clear of the Azimuth arrow, which reaches past the log on short shafts.
    const tableX = 4 * ArcL + padding + 110;
    const noteLine = (yPos, txt, opts = {}) => {
      const n = new PointText(new Point(tableX + (opts.indent || 0), yPos));
      n.justification = "left";
      n.fillColor = "black";
      if (opts.bold) n.fontWeight = "bold";
      n.fontSize = opts.fontSize || 9;
      n.content = txt;
      return n;
    };

    let curY = points + padding * 2;

    // Inputs section
    const inLabel = new PointText(new Point(tableX, curY));
    inLabel.justification = "left";
    inLabel.fillColor = "black";
    inLabel.fontWeight = "bold";
    inLabel.content = "Inputs:";
    curY += 18;
    const geomText = new PointText(new Point(tableX, curY));
    geomText.justification = "left";
    geomText.fillColor = "black";
    geomText.content =
      "View Direction: N" + String(Math.round(D)).padStart(3, "0") + "\xB0" +
      "; Shaft Radius: " + usrvar["Shaft Radius"];
    curY += 20;

    const inColDefs = [
      { key: "no", label: "D.No.", width: 55 },
      { key: "dmax", label: "MaxDepth", width: 80 },
      { key: "dmin", label: "MinDepth", width: 80 },
      { key: "dis", label: "Dis", width: 60 },
      { key: "pos", label: "Position", width: 140 },
    ];
    const inCols = buildCols(tableX, inColDefs);
    const inLeftX = inCols[0].left;
    const inRightX = inCols[inCols.length - 1].right;
    const inHeaderY = curY;
    const inDataY0 = inHeaderY + 18;
    const inTableBottomY = inDataY0 + 18 * inputRows.length;

    inCols.forEach((col) => {
      cellText(col.label, col.left, col.right, inHeaderY, inDataY0, { bold: true });
    });
    inputRows.forEach((row, r) => {
      const yT = inDataY0 + r * 18;
      const yB = yT + 18;
      inCols.forEach((col) => {
        cellText(row[col.key] != null ? String(row[col.key]) : "-", col.left, col.right, yT, yB, { color: row.color });
      });
    });
    drawBorder(inLeftX, inHeaderY, inRightX, inHeaderY);
    drawBorder(inLeftX, inTableBottomY, inRightX, inTableBottomY);
    drawBorder(inLeftX, inHeaderY, inLeftX, inTableBottomY);
    drawBorder(inRightX, inHeaderY, inRightX, inTableBottomY);
    inCols.forEach((col, i) => {
      if (i === 0) return;
      drawBorder(col.left, inHeaderY, col.left, inTableBottomY);
    });
    drawBorder(inLeftX, inDataY0, inRightX, inDataY0);
    curY = inTableBottomY + 14;

    const inputNotesLabel = noteLine(curY, "Notes:", { bold: true });
    noteLine(curY, " D.No. (Discontinuity number); MaxDepth (Max depth of trace); MinDepth (Min depth of trace); Dis (Distance of MaxDepth from", {
      indent: inputNotesLabel.bounds.width + 2,
    });
    curY += 12;
    noteLine(curY, "centreline); Position (Position of MaxDepth wrt centreline).");
    curY += 22;

    // Outputs section
    const outLabel = new PointText(new Point(tableX, curY));
    outLabel.justification = "left";
    outLabel.fillColor = "black";
    outLabel.fontWeight = "bold";
    outLabel.content = "Outputs:";
    curY += 18;
    const circText = new PointText(new Point(tableX, curY));
    circText.justification = "left";
    circText.fillColor = "black";
    circText.content =
      "Circumference: " + (2 * Math.PI * parseFloat(usrvar["Shaft Radius"])).toFixed(1).replace(/\.0$/, "");
    curY += 20;

    const outColDefs = [
      { key: "no", label: "D.No.", width: 55 },
      { key: "dir", label: "DipDir", width: 70 },
      { key: "amt", label: "DipAmt", width: 70 },
      { key: "tsp", label: "TSp", width: 60 },
      { key: "asf", label: "Asf", width: 60 },
    ];
    const outCols = buildCols(tableX, outColDefs);
    const outLeftX = outCols[0].left;
    const outRightX = outCols[outCols.length - 1].right;
    const outHeaderY = curY;
    const outDataY0 = outHeaderY + 18;
    const outTableBottomY = outDataY0 + 18 * outputRows.length;

    outCols.forEach((col) => {
      cellText(col.label, col.left, col.right, outHeaderY, outDataY0, { bold: true });
    });
    outputRows.forEach((row, r) => {
      const yT = outDataY0 + r * 18;
      const yB = yT + 18;
      outCols.forEach((col) => {
        const v = row[col.key];
        cellText(v != null ? String(v) : "-", col.left, col.right, yT, yB, { color: row.color });
      });
    });
    drawBorder(outLeftX, outHeaderY, outRightX, outHeaderY);
    drawBorder(outLeftX, outTableBottomY, outRightX, outTableBottomY);
    drawBorder(outLeftX, outHeaderY, outLeftX, outTableBottomY);
    drawBorder(outRightX, outHeaderY, outRightX, outTableBottomY);
    outCols.forEach((col, i) => {
      if (i === 0) return;
      drawBorder(col.left, outHeaderY, col.left, outTableBottomY);
    });
    drawBorder(outLeftX, outDataY0, outRightX, outDataY0);
    curY = outTableBottomY + 14;

    // The persistence caution reads first, directly under the Outputs table.
    const cautionLabel = noteLine(curY, "Caution:", { bold: true });
    noteLine(curY, " Discontinuity trace span may be evaluated with care in consideration of its persistence.", {
      indent: cautionLabel.bounds.width + 2,
    });
    curY += 16;

    const outputNotesLabel = noteLine(curY, "Notes:", { bold: true });
    noteLine(curY, " DipDir (Dip direction); DipAmt (Dip amount); TSp (Trace span); Asf (Apparent Spacing Factor).", {
      indent: outputNotesLabel.bounds.width + 2,
    });
    curY += 16;

    // Asf usage notes; the last one links to the TrueSpace tool.
    noteLine(curY, "•  Divide True Spacing and/or True Thickness by Apparent Spacing Factor (Asf) to get Apparent Spacing/");
    curY += 12;
    noteLine(curY, "Apparent Thickness along the Shaft.", { indent: 11 });
    curY += 12;
    trueSpaceLinkBounds.current = drawTrueSpaceNote(
      noteLine,
      curY,
      "•  If True Spacing/Thickness not available, get True Spacing/Thickness from this app: ",
      Path
    );
    curY += 16;

    if (paper.view.viewSize.height < curY + 30) {
      paper.view.viewSize = new Size(paper.view.viewSize.width, curY + 30);
    }

  };
  const draw = () => {
    createScale();
    createLog();
    createTrace();
  };
  return (
    <div id="page">
      <Header onBack={props.showinput} />
      <Footer />
      <div className="page-content header-clear-medium">
        <div className="card card-style contact-form">
          <div className="content">
            <button
              onClick={() => {
                props.showinput();
              }}
              className="btn bg-green-dark mw-2 me-3"
            >
              Edit Input
            </button>
            <DownloadMenu filename="shaft-heading" screenLabel="Shaft — Heading" feedbackInputs={props.dimensions} />
            <div className="row">
              <div className="col-md-12 overflow-auto">
                <canvas id="paper-canvas" resize="true" onClick={handleCanvasClick} onMouseMove={handleCanvasMouseMove} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Output;
